/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
import type { PanelItem } from '../../../panel-items/panel-item.class';

import { ActionRuleParams } from '../../types';
import { countertopSpacing } from './countertop-spacing';
import { formatValidation, getIslandLetter } from '../island-validation-format';
import { getSurroundFinishProfile, nearestSixteenth } from '../../../../helpers';
import { libLang } from '../../../../lang'
import { NotificationType } from '../../../notifications/enums';
// import { usePanel } from '@rta/rta-blueprint-component'

type Intersects = {
	id: string;
	itemRefId: string;
	item: string;
	iDescription: string;
	pLabel: string;
	size: number;
	limit: number;
	isFridgeAboveSpacing?: boolean;
}

const FRIDGE_SPACING_REQUIRED = libLang.validations.island.panel.fridgeSpacing;

export const panelEdgeLimit = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { islands } = refMap
	const { notifications } = refMap.kitchen.design
	const minDistanceFromEdge = 5;
	const minDistanceFromApps = 4.5;
	const defaultMinDistanceFromAppsVertical = 2.4375;
	// Concrete-only: panel that must remain above a fridge cutout, replacing the generic vertical-edge gap for that exact combination only.
	const minPanelAboveFridge = 2.5;

	const badIntersects: Intersects[] = []
	const allItems: Record<string, string> = {};
	const allPanelItems: Record<string, PanelItem> = {};

	notifications.purgeReferences(libLang.validations.island.panel.spacing);
	notifications.purgeReferences(FRIDGE_SPACING_REQUIRED);

	if (!islands) return

	islands.children.forEach((island) => {
		const { minVerticalEdgeGap = defaultMinDistanceFromAppsVertical } = getSurroundFinishProfile(island.surround?.material) ?? {};
		const isConcreteSurround = island.surround?.material === 'concrete';

		island.panels.children.forEach((panel) => {
			const baseHorizontalIntersects = panel.getHorizontalIntersectionAreas({ inside: true, includeDuplicates: false, includeEnd2End: false, minHeight: 0.5 })
				.map((area) => ({
					...area,
					shouldHide: false
					// dimension: kitchen.design.dimensions.find(area.InterectionReferenceIds, area.axis, true) as Dimension
				}));

			const baseVerticalIntersects = panel.getVerticalIntersectionAreas({ inside: true, includeDuplicates: false, includeEnd2End: false, minWidth: 0.5 })
				.map((area) => ({
					...area,
					shouldHide: false
					// dimension: kitchen.design.dimensions.find(area.InterectionReferenceIds, area.axis, true) as Dimension
				}));

			panel.panelItemGroups.children.forEach((group) => {
				group.items.children.forEach((item) => {
					allItems[item.id] = item.item.sku
					allPanelItems[item.id] = item
				})
			})

			const panelEdge = panel.id;

			if (panelEdge) {
				baseHorizontalIntersects.forEach((hi) => {
					const width = nearestSixteenth(hi.size.width);
					const [firstId] = hi.intersectionReferenceIds;
					const [, secondId] = hi.intersectionReferenceIds;
					const currentItemId = firstId === panelEdge ? secondId : firstId
					const currentItem = allItems[currentItemId]
					if (width > 0 && ((hi.intersectionReferenceIds.includes(panelEdge) && width < minDistanceFromEdge) || (!hi.intersectionReferenceIds.includes(panelEdge) && width < minDistanceFromApps))) {
						badIntersects.push({
							id: panelEdge,
							itemRefId: currentItemId,
							item: currentItem,
							iDescription: island.description,
							pLabel: panel.label,
							size: width,
							limit: (!hi.intersectionReferenceIds.includes(panelEdge) && width < minDistanceFromApps) ? minDistanceFromApps : minDistanceFromEdge
						})
					}
				})
				baseVerticalIntersects.forEach((vi) => {
					const height = nearestSixteenth(vi.size.height);
					const [firstId] = vi.intersectionReferenceIds;
					const [, secondId] = vi.intersectionReferenceIds;
					const currentItemId = firstId === panelEdge ? secondId : firstId
					// const currentItem = allItems[currentItemId]
					// only the gap against a panel edge is finish-aware; appliance-to-appliance clearance is the same on every finish
					const minDistanceFromAppsVertical = vi.intersectionReferenceIds.includes(panelEdge) ? minVerticalEdgeGap : defaultMinDistanceFromAppsVertical;
					// The sweep walks top-to-bottom, so firstId === panelEdge is specifically the gap above this item (never below it).
					const isAboveGap = firstId === panelEdge;
					const isFridgeAboveOnConcrete = isAboveGap && isConcreteSurround && (allPanelItems[currentItemId]?.item?.category?.isFridge ?? false);
					const effectiveLimit = isFridgeAboveOnConcrete ? minPanelAboveFridge : minDistanceFromAppsVertical;
					if (height > 0 && height < effectiveLimit) {
						const fullHeight = panel.panelItemGroups.children.some((group): boolean => {
							if (group.items.children.length > 0) {
								const [badItemId] = vi.intersectionReferenceIds.filter((i) => i && i !== panelEdge)
								const currentItem = group.items.children.find((item) => item.id === badItemId)

								return currentItem?.isFullHeightAppliance ?? null
							}
							return false
						})
						if (!fullHeight) {
							badIntersects.push({
								id: panelEdge,
								itemRefId: currentItemId,
								item: allItems[currentItemId],
								iDescription: island.description,
								pLabel: panel.label,
								size: height,
								limit: effectiveLimit,
								isFridgeAboveSpacing: isFridgeAboveOnConcrete
							})
						}
					}
				})
			}
			// console.log('horizontalIntersects', baseHorizontalIntersects, 'verticalIntersects', baseVerticalIntersects, 'panelEdge', panelEdge);
		})
	})
	// console.log('badIntersects', refMap)
	badIntersects.forEach((bi) => {
		const island = islands.children.find((isl) => isl.panels.children.some((p) => p.id === bi.id));
		if (bi.isFridgeAboveSpacing) {
			notifications.add({
				refId: bi.id,
				itemRefId: bi.itemRefId,
				validationCode: FRIDGE_SPACING_REQUIRED,
				description: formatValidation({ letter: island ? getIslandLetter(island) : '', islandDescription: bi.iDescription, location: bi.pLabel, issue: `${bi.item ?? 'Item'} needs at least ${bi.limit}" of panel above the fridge cutout — current ${bi.size}". Raise the base height to restore the gap, or cut the fridge to the full base height for a visible gap above it.` }),
				type: NotificationType.Warn
			})
			return;
		}
		notifications.add({
			refId: bi.id,
			itemRefId: bi.itemRefId,
			validationCode: libLang.validations.island.panel.spacing,
			description: formatValidation({ letter: island ? getIslandLetter(island) : '', islandDescription: bi.iDescription, location: bi.pLabel, issue: `${bi.item ?? 'Item'} needs more spacing — current ${bi.size}", minimum ${bi.limit}"` }),
			type: NotificationType.Warn
		})
	})
	countertopSpacing(params);
}
